import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  WhatsApp as WhatsAppIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Star as StarIcon,
  BookOnline as BookIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Assignment as ProgressIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `help-tab-${index}`,
    'aria-controls': `help-tabpanel-${index}`,
  };
}

const FAQ_ITEMS = [
  {
    category: 'whatsapp',
    question: 'Why is my WhatsApp QR Code not loading or failing to pair?',
    answer: 'The QR Code is generated dynamically by WhapiCell. If it fails to load, try refreshing the WhatsApp page. Ensure you do not have another active browser session connected to the same Whapi instance. If the issue persists, click "Disconnect Instance", wait 10 seconds, and click "Reconnect" to generate a fresh QR Code.',
    role: 'Instructor'
  },
  {
    category: 'notifications',
    question: 'Are lesson reminders sent to students automatically?',
    answer: 'Yes! Once you set up your WhatsApp connection, the system runs a background job. It automatically schedules and sends a reminder to students exactly 1 day before their scheduled lesson. To prevent WhatsApp spam filters, messages are sent at varying times throughout the day.',
    role: 'Instructor'
  },
  {
    category: 'calendar',
    question: 'How do I add or block time slots in my availability?',
    answer: 'Navigate to "Add Availability" from the sidebar. You can select a date, specify starting and ending hours, set the cost per hour, and choose whether to repeat this slot. If you need to remove availability, you can view your schedule from the dashboard and delete or block specific slots.',
    role: 'Instructor'
  },
  {
    category: 'booking',
    question: 'How do I book a lesson with my instructor?',
    answer: 'Go to "Book a Lesson" from the sidebar. Select your instructor (if you have multiple, or your assigned instructor), browse their calendar for open time slots, select a slot that fits your schedule, and click "Book". You will receive an instant WhatsApp confirmation once the booking is completed.',
    role: 'Student'
  },
  {
    category: 'progress',
    question: 'What is DVSA Progress Tracking and how do I use it?',
    answer: 'The DVSA (Driver and Vehicle Standards Agency) syllabus consists of 19 core driving topics (e.g., Junctions, Maneuvers, Dual Carriageways). Instructors can grade a student\'s performance on each topic from 1 to 5 (1: Introduced, 2: Understood, 3: Prompted, 4: Independent, 5: Test Ready). Students can view this live progress bar on their Student Dashboard.',
    role: 'Both'
  },
  {
    category: 'billing',
    question: 'How do subscription payments work?',
    answer: 'Instructors can access the "Subscription" page to unlock full scheduling features. We offer monthly and yearly subscription plans processed securely via PayPal. You can cancel or change your plan at any time through your subscription settings page.',
    role: 'Instructor'
  },
  {
    category: 'profile',
    question: 'How can I change my registered phone number or password?',
    answer: 'Instructors can update student phone numbers under "Student Management". Students can update their own phone numbers and profile details in "My Profile". Passwords can also be updated from the profile settings. Note that valid phone numbers must include the country code (e.g. +447000000000) for WhatsApp messaging to function.',
    role: 'Both'
  }
];

export const HelpPage: React.FC = () => {
  const { user } = useAuth();
  
  // Set default tab based on user role
  const defaultTab = user?.role === 'Student' ? 1 : 0;
  
  const [tabValue, setTabValue] = useState(defaultTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: '',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSupportChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSupportForm({
      ...supportForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleSupportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportForm.subject || !supportForm.message) return;
    
    // Simulate support submission
    console.log('Support ticket submitted:', supportForm);
    setSupportSubmitted(true);
    setSupportForm({ subject: '', message: '' });
  };

  // Filter FAQs based on search query
  const filteredFaqs = FAQ_ITEMS.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Premium Header Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          mb: 4,
          boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 64, height: 64, mb: 2 }}>
          <HelpIcon sx={{ fontSize: 36, color: 'white' }} />
        </Avatar>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          adiLessons Help Center
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.9, maxWidth: 600, mb: 3 }}>
          Find detailed setup manuals, frequently asked questions, and get instant technical assistance for your driving management portal.
        </Typography>
        
        {/* Search bar inside header */}
        <TextField
          placeholder="Search help articles, guides, or FAQs..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: '100%',
            maxWidth: 500,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs Layout */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="help center tabs"
            textColor="primary"
            indicatorColor="primary"
            sx={{ px: 2 }}
          >
            <Tab label="Instructor Manual" {...a11yProps(0)} sx={{ fontWeight: 'bold', py: 2 }} />
            <Tab label="Student Manual" {...a11yProps(1)} sx={{ fontWeight: 'bold', py: 2 }} />
            <Tab label="FAQs" {...a11yProps(2)} sx={{ fontWeight: 'bold', py: 2 }} />
            <Tab label="Contact Support" {...a11yProps(3)} sx={{ fontWeight: 'bold', py: 2 }} />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 4 } }}>
          {/* Instructor Manual Tab */}
          <CustomTabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  Instructor Comprehensive Guide
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Welcome to adiLessons! As an instructor, this platform allows you to automate bookings, send automated WhatsApp reminders, and track student success with zero manual effort. Follow the steps below to set up your profile:
                </Typography>
                
                <List sx={{ mt: 2 }}>
                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#2e7d32', color: 'white' }}>
                        <WhatsAppIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          1. Connect WhatsApp Automation
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Navigate to the <strong>WhatsApp Connection</strong> tab. Scan the displayed QR Code with your WhatsApp app (Linked Devices menu). Once connected, the system will automatically handle reminder messages, booking confirmations, and status alerts.
                        </Typography>
                      }
                    />
                  </ListItem>
                  
                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#1976d2', color: 'white' }}>
                        <CalendarIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          2. Set Up Your Time Slots
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Use the <strong>Add Availability</strong> screen to populate your calendar. You can define lesson rates and durations. Active students will see these slots in real time and can select them to book driving lessons.
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                        <PeopleIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          3. Add & Manage Students
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Under <strong>Student Management</strong>, add your students by specifying their phone numbers (including country codes, e.g. <i>+447...</i>). The system automatically registers their accounts and sends their login credentials via WhatsApp.
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#ed6c02', color: 'white' }}>
                        <ProgressIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          4. Track DVSA Progress Ratings
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          During or after each lesson, update the student's rating on the 19 core DVSA topics. Rating them from 1 to 5 gives them visual tracking on their dashboard, boosting engagement and driving confidence.
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: '#f0f4c3', border: '1px solid #c5e1a5', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom display="flex" alignItems="center">
                      <StarIcon color="warning" sx={{ mr: 1 }} /> Premium Tip
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Always double-check that student phone numbers include country codes (e.g. +44 for UK, +90 for Turkey) before registering them. The WhatsApp API (WhapiCell) cannot process messages without valid international routing.
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Make sure your subscription is active in the <strong>Subscription</strong> page to avoid schedule locking or messaging downtime.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CustomTabPanel>

          {/* Student Manual Tab */}
          <CustomTabPanel value={tabValue} index={1}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  Student Portal Guide
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Welcome to your student learning hub! Here you can check your driving lesson schedule, book new slots, and review your driving skill level as you prepare for your DVSA test.
                </Typography>

                <List sx={{ mt: 2 }}>
                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#1976d2', color: 'white' }}>
                        <BookIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Booking Driving Lessons
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          To book, click <strong>Book a Lesson</strong> in the sidebar. You will see your instructor's open calendar slots. Select an available time, confirm the pricing, and click book. A confirmation message will be instantly sent to you via WhatsApp.
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#2e7d32', color: 'white' }}>
                        <ProgressIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Viewing Your DVSA Progress
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Your student dashboard shows a live status breakdown of the 19 standard DVSA driving components. As your instructor grades you, watch your progress bar climb towards 100% (Test Ready).
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#ffb74d', color: 'white' }}>
                        <WhatsAppIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          WhatsApp Reminder Messages
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          You don't need to worry about forgetting your appointment! Our platform dispatches automated reminders directly to your WhatsApp exactly 1 day prior to the lesson, noting the date, time, and meeting details.
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem alignItems="flex-start" sx={{ mb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Managing Your Profile
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Keep your phone number updated in <strong>My Profile</strong>. If you change your phone number, inform your instructor so they can verify that the notifications are set correctly on your account.
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom display="flex" alignItems="center">
                      <CheckCircleIcon color="primary" sx={{ mr: 1 }} /> Lesson Cancellation
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Please check with your instructor regarding their lesson cancellation and rescheduling policy. Typically, changes must be requested at least 24 to 48 hours prior to the lesson to avoid forfeiting lesson credits.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CustomTabPanel>

          {/* FAQs Tab */}
          <CustomTabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Frequently Asked Questions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredFaqs.length} questions matching your criteria. Use the search bar above to filter answers.
              </Typography>
            </Box>

            <Box>
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 2,
                      borderRadius: '8px !important',
                      border: '1px solid #e0e0e0',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon color="primary" />}
                      aria-controls={`faq-content-${index}`}
                      id={`faq-header-${index}`}
                      sx={{ py: 1 }}
                    >
                      <Box display="flex" alignItems="center">
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.75rem',
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                            mr: 2,
                          }}
                        >
                          Q
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          {faq.question}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 1, bgcolor: '#fafafa' }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {faq.answer}
                      </Typography>
                      <Box display="flex" mt={2}>
                        <Box
                          sx={{
                            fontSize: '0.7rem',
                            color: 'white',
                            bgcolor: 'text.secondary',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            fontWeight: 'bold',
                          }}
                        >
                          Target: {faq.role}
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    No matching FAQs found for "{searchQuery}".
                  </Typography>
                  <Button variant="text" color="primary" onClick={() => setSearchQuery('')}>
                    Clear Search Filter
                  </Button>
                </Box>
              )}
            </Box>
          </CustomTabPanel>

          {/* Contact Support Tab */}
          <CustomTabPanel value={tabValue} index={3}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                  Submit a Support Ticket
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Can't find what you are looking for? Fill out the form below and our engineering team will get back to you within 24 hours.
                </Typography>

                {supportSubmitted ? (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: '#e8f5e9',
                      border: '1px solid #a5d6a7',
                      borderRadius: 2,
                    }}
                  >
                    <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
                      Ticket Submitted Successfully!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Thank you for contacting us. A confirmation has been logged, and we will contact you at <strong>{user?.email}</strong>.
                    </Typography>
                    <Button variant="outlined" color="success" onClick={() => setSupportSubmitted(false)}>
                      Send Another Ticket
                    </Button>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSupportSubmit}>
                    <TextField
                      fullWidth
                      label="Your Email"
                      value={user?.email || ''}
                      disabled
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={supportForm.subject}
                      onChange={handleSupportChange}
                      required
                      placeholder="e.g. WhatsApp Pairing Issue"
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label="Detailed Message"
                      name="message"
                      value={supportForm.message}
                      onChange={handleSupportChange}
                      required
                      multiline
                      rows={5}
                      placeholder="Please describe your problem or question in detail..."
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      endIcon={<SendIcon />}
                      fullWidth
                      sx={{ py: 1.5, borderRadius: 2 }}
                    >
                      Submit Ticket
                    </Button>
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    p: 4,
                    bgcolor: '#fafafa',
                    border: '1px dashed #ccc',
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.light', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    <EmailIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Direct Email Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You can also email us directly at our global support desk:
                  </Typography>
                  <Typography 
                    component="a" 
                    href="mailto:support@adilesson.co.uk" 
                    variant="subtitle1" 
                    fontWeight="bold" 
                    color="primary"
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    support@adilesson.co.uk
                  </Typography>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="caption" color="text.secondary">
                    Response time standard is 1 business day. Support is available in English.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CustomTabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default HelpPage;
